/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import './style/viewer.css';

import { createAccordion } from './lib/accordion.js';
import { keepAspectRatio, scrollToY } from './lib/dom.js';
import { videoLightbox } from './lib/dialog.js';
import { createGallery } from './lib/gallery.js';
import { Glossary } from './lib/glossary.js';
import { setLanguage } from './lib/i18n.js';
import {
    injectParams,
    isEmbedded,
    isExternalLink,
} from './lib/links.js';
import { linksToMediaPlayer } from './lib/media.js';
import { Social } from './lib/social.js';
import { formatTimes } from './lib/time.js';
import { cookie } from './lib/cookie.js';

/*
 * The article page.
 *
 * `/<map_id>/article/<id>` is a page in its own right so a crawler can
 * index one article, and a browser that lands on it is handed back to the
 * map with the article open. It is not the Map Viewer, but it
 * shares most of its behaviour.
 *
 * It replaces roughly three hundred lines of script written inline across
 * three templates, which pulled in jQuery, jQuery UI and XRegExp for a
 * page of prose. Two of those templates carried their own copy of the
 * gallery builder.
 */

/** Read the page's configuration island. */
function readConfig() {
    const element = document.getElementById('article-config');
    try {
        return JSON.parse(element?.textContent ?? '{}');
    } catch (error) {
        console.error(`Error parsing article configuration: ${error}`);
        return {};
    }
}

/**
 * Hand a browser back to the map, with this article open.
 *
 * The page exists to be indexed, not to be read on its own: a reader who
 * follows a search result should arrive at the map. An embedded article
 * is already where it belongs, and so is one the server asked to leave
 * alone.
 *
 * @param {boolean} prevented - Whether the server suppressed the redirect.
 */
function redirectToMap(prevented) {
    if (prevented || isEmbedded()) return;

    const { pathname, origin } = window.location;
    const match = pathname.match(/\/article\/(\d+)$/);
    if (!match) return;

    const hash = `#a=${match[1]}`;
    window.parent.postMessage(hash, '*');
    window.location.assign(
        origin + pathname.replace(/\/article\/\d+$/, '') + hash
    );
}

/**
 * Fit the title area to the window it is being read in.
 *
 * The heading is a full-height panel above the article, and an embedded
 * article is read through a frame whose size the map decides, so the
 * measurement has to come from whichever window is on screen.
 *
 * @param {HTMLElement} heading - The article's header.
 */
function sizeHeading(heading) {
    if (!heading) return;

    const embedded = isEmbedded();
    const main = embedded ? window.parent : window;
    const headerBar = document.getElementById('header');
    const available = main.innerHeight - (headerBar?.offsetHeight || 0);
    const rem =
        Number.parseFloat(getComputedStyle(document.documentElement).fontSize) ||
        16;

    if (!embedded) {
        heading.style.maxWidth = `${main.innerWidth}px`;
        heading.style.maxHeight = `${available - rem}px`;
        return;
    }

    // Room for the map's zoom bar and the arrow that closes the article.
    const zoomBar = 0.5 * rem;
    const arrow = 27;
    const landscape =
        main.innerWidth > 600 && main.innerWidth >= main.innerHeight;

    heading.style.maxWidth = landscape
        ? `${main.innerWidth * 0.85 - arrow - zoomBar}px`
        : `${main.innerWidth - zoomBar}px`;
    heading.style.maxHeight = landscape
        ? `${available - rem}px`
        : `${available * 0.75 - rem}px`;
}

/**
 * Apply the inline style an author wrote into an image's title.
 *
 * The Map Builder offers no style field, so `{...}` at the front of the title
 * is read as one and the rest stays the caption.
 *
 * @param {HTMLElement} content - The article body.
 */
function applyFigureStyles(content) {
    content.querySelectorAll('img[title]').forEach((image) => {
        const match = image.getAttribute('title').match(/^{(.*)}\s*([\s\S]*)$/);
        if (!match) return;
        image.setAttribute('style', match[1]);
        image.setAttribute('title', match[2]);
    });
}

/**
 * Carry the reader's access token and preview mode across the article.
 *
 * A token-gated map hands its token to everything it links to; without
 * it, every image and link in the article answers with a login page.
 *
 * @param {HTMLElement} content - The article body.
 * @param {string|null} accessToken - The token, if the reader has one.
 * @param {boolean} isPreview - Whether this is a preview of a draft.
 */
function carryAccess(content, accessToken, isPreview) {
    const media =
        'img[src], audio[src], video[src],' +
        ' audio > source[src], video > source[src]';
    content.querySelectorAll(media).forEach((element) => {
        if (isExternalLink(element.src)) return;
        element.src = injectParams(element.src, { access: accessToken });
    });

    content.querySelectorAll('a[href]').forEach((link) => {
        // A link into this page needs no token, and resolving it to an
        // absolute URL would stop it reading as a fragment — which is how
        // footnotes are found, so they silently stopped being footnotes.
        if (link.getAttribute('href')?.startsWith('#')) return;
        if (isExternalLink(link.href)) return;
        try {
            const url = new URL(link.href);
            if (isPreview) {
                url.searchParams.append('preview', 1);
            } else {
                url.searchParams.delete('preview');
            }
            link.href = injectParams(url.toString(), { access: accessToken });
        } catch (error) {
            console.error(error);
        }
    });
}

/**
 * Turn footnote links into footnotes.
 *
 * The definition lives in the link's title, which the browser would show
 * as its own tooltip, so it is moved to a data attribute the stylesheet
 * draws instead.
 *
 * @param {HTMLElement} content - The article body.
 */
function markFootnotes(content) {
    content.querySelectorAll('a[href^="#footnote"]').forEach((link) => {
        link.classList.add('footnote');
        link.addEventListener('click', (event) => event.preventDefault());
        link.dataset.footnote = link.getAttribute('title') || '';
        link.setAttribute('title', '');
    });
}

/**
 * Send links off this site to a window of their own.
 *
 * @param {HTMLElement} article - The article element.
 */
function openExternalLinksElsewhere(article) {
    article
        .querySelectorAll('a[href^="http://"], a[href^="https://"]')
        .forEach((link) => {
            if (isExternalLink(link.getAttribute('href'))) {
                link.setAttribute('target', '_blank');
            }
        });
}

/**
 * Keep embedded media inside the column it sits in.
 *
 * Authors paste `width` and `height` from wherever they copied the embed,
 * and those are usually wider than the page.
 *
 * @param {HTMLElement} content - The article body.
 */
function sizeEmbeddedMedia(content) {
    content.querySelectorAll('iframe').forEach((frame) => {
        const width = Number.parseInt(frame.getAttribute('width'), 10) || 16;
        const height = Number.parseInt(frame.getAttribute('height'), 10) || 9;
        keepAspectRatio(frame, width / height, 'width', content);
        frame.removeAttribute('width');
        frame.removeAttribute('height');
    });
}

/**
 * Point the share links at the page a reader would actually send.
 *
 * @param {object} services - Which services this map offers.
 */
function setUpSharing(services) {
    const social = new Social(services || {});
    let href = window.location.href;
    try {
        href = window.top.location.href;
    } catch {
        // Cross-origin embed: this frame's URL is the best available.
    }

    document.querySelectorAll('.social-share-link').forEach((link) => {
        const service = link.dataset.serviceId;
        const url = social.getServiceLinkUrl(
            service,
            href,
            link.dataset.shareTitle,
            link.dataset.shareText
        );
        if (!url) return;
        link.setAttribute('href', url);
        link.setAttribute('target', service === 'email' ? '_top' : '_blank');
    });
}

/**
 * Build the collapsible lists and galleries an author marked up.
 *
 * A list is a gallery when a `.start-gallery` precedes it, an accordion
 * when it says so or the article is an interview — where every odd item
 * is a question and every even one the answer to it.
 *
 * @param {HTMLElement} content - The article body.
 * @param {boolean} isInterview - Whether every list is a Q-and-A.
 */
function buildLists(content, isInterview) {
    content.querySelectorAll(':scope > ul').forEach((list) => {
        const previous = list.previousElementSibling;

        if (previous?.classList.contains('start-gallery')) {
            createGallery(list);
            return;
        }

        const wanted =
            (list.classList.contains('accordion') &&
                !list.parentElement?.classList.contains('accordion')) ||
            previous?.classList.contains('start-accordion') ||
            isInterview;
        if (!wanted) return;

        [...list.children].forEach((item, index) => {
            item.classList.add(index % 2 === 0 ? 'question' : 'answer');
        });
        createAccordion(list, { collapsible: true, active: -1 });

        // Opening a section further down moves everything below it; the
        // heading that was just clicked should stay where it was read.
        list.addEventListener('click', (event) => {
            const toggle = event.target.closest('.accordion-toggle');
            if (toggle?.getAttribute('aria-expanded') === 'true') {
                scrollToY(document.documentElement, toggle, -50);
            }
        });
    });
}

/**
 * Give the media article's poster link the embed hidden beside it.
 *
 * @param {HTMLElement} article - The article element.
 */
function setUpMediaArticle(article) {
    const videoLink = article.querySelector('article .start a');
    if (!videoLink) return;

    const embedLink = [
        ...article.querySelectorAll('.article-content > main > p a'),
    ].find((a) => (a.textContent || '').includes('embedded'));

    if (embedLink) {
        const attributes = {};
        const title = (embedLink.getAttribute('title') || '').replace(
            /\[(\w+)='([^']*)']/g,
            (match, name, value) => {
                attributes[name] = value;
                return '';
            }
        );
        attributes.title = title;
        videoLink.setAttribute('href', embedLink.getAttribute('href'));
        videoLink.dataset.attrs = JSON.stringify(attributes);
        embedLink.remove();
    }

    videoLightbox(article, videoLink);
}

/** Set the page up. */
export async function init() {
    const config = readConfig();
    setLanguage(config.lang || document.documentElement.lang);

    redirectToMap(config.preventRedirect);

    const article = document.getElementById('map');
    const content = document.querySelector('.article-content > main');
    if (!article || !content) return;

    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access') || cookie.read('access_token');
    // The server's answer, not the address. A reader can arrive at
    // `?preview=1` and be served the published article, and every link on
    // it has to say published too -- otherwise following one lands them on
    // a login form for a version they were never looking at.
    const isPreview = !!config.isPreview;

    // The article's date is UTC and sits in the header, outside the body
    // every other pass works on.
    formatTimes(document);

    sizeHeading(article.querySelector('article > header'));
    applyFigureStyles(content);
    carryAccess(content, accessToken, isPreview);
    markFootnotes(content);
    linksToMediaPlayer(content);
    openExternalLinksElsewhere(article);
    sizeEmbeddedMedia(content);
    setUpSharing(config.social);

    if (config.layout === 'media') {
        setUpMediaArticle(article);
    }
    buildLists(
        content,
        !!article.querySelector(
            ':scope > article.interview, :scope > article.video-interview'
        )
    );

    if (config.glossaryUrl) {
        const glossary = new Glossary();
        await glossary.load(config.glossaryUrl);
        glossary.linkTerms(content);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    init();
});
